import React, { useState, useEffect, Suspense, lazy, useRef } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import Person from './Person';
import NavBar from './NavBar';
import Sidebar from './Sidebar';
import Flatpickr from 'react-flatpickr';
import 'flatpickr/dist/themes/material_blue.css';
import Summary from './Summary';
import ProgressBar from './ProgressBar';
import { CITIZENSHIP_STATUS } from './constants';
import './App.css';

const NextSteps = lazy(() => import('./NextSteps'));
const BillC3Info = lazy(() => import('./BillC3Info'));
const Resources = lazy(() => import('./Resources'));
const FAQ = lazy(() => import('./FAQ'));
const DocumentChecklist = lazy(() => import('./DocumentChecklist'));
const PassportGuide = lazy(() => import('./PassportGuide'));
const SettlingIn = lazy(() => import('./SettlingIn'));
const About = lazy(() => import('./About'));
const LandAcknowledgement = lazy(() => import('./LandAcknowledgement'));

const makeDefaultPerson = () => ({
  id: 1,
  name: '',
  birthYear: new Date(1990, 0, 1),
  isCitizen: CITIZENSHIP_STATUS.NO,
  citizenshipDate: null,
  ancestors: [],
  threeYearsCumulative: false,
  documentation: {},
  calculatedStatus: null,
  crownServant: false,
  alsoApplying: false,
});


const AppContent = () => {
  const [showInlineDocs, setShowInlineDocs] = useState(false);
  const [people, setPeople] = useState([makeDefaultPerson()]);
  const summaryRef = useRef(null);

  const actionButtonStyle = {
    padding: '8px 16px',
    borderRadius: '6px',
    border: '1px solid #d1d5db',
    backgroundColor: 'white',
    cursor: 'pointer',
    fontSize: '0.9rem',
    color: '#4b5563',
    minWidth: '190px',
    textAlign: 'center',
  };

  const primaryActionButtonStyle = {
    ...actionButtonStyle,
    backgroundColor: '#2563eb',
    color: 'white',
    border: '1px solid #2563eb',
    fontWeight: '600',
  };

  const location = useLocation();
  const navigate = useNavigate();

  // Derive active state from URL
  const getActiveState = () => {
    const path = location.pathname;
    if (path.startsWith('/descent')) return { category: 'descent', view: path.split('/')[2] || 'checker' };
    if (path.startsWith('/after')) return { category: 'after', view: path.split('/')[2] || 'passport' };
    if (path.startsWith('/land_acknowledgement')) return { category: 'land_acknowledgement', view: '' };
    if (path.startsWith('/about')) return { category: 'about', view: '' };
    return { category: 'home', view: '' };
  };

  const { category, view } = getActiveState();

  // Responsive State
  const [isMobile, setIsMobile] = useState(window.innerWidth < 1024);

  const [useExactDates, setUseExactDates] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showDisclaimer, setShowDisclaimer] = useState(false);
  const [hasSeenDisclaimer, setHasSeenDisclaimer] = useState(false);

  // Modal State
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [pendingAncestorId, setPendingAncestorId] = useState(null);
  const [showCascadeModal, setShowCascadeModal] = useState(false);
  const [cascadeMessage, setCascadeMessage] = useState('');
  const [cascadeActions, setCascadeActions] = useState(null);
  const [cascadeButtonLabels, setCascadeButtonLabels] = useState({ yes: 'Yes', no: 'No' });

  // New state for residency modal
  const [showResidencyModal, setShowResidencyModal] = useState(false);
  const [residencyModalData, setResidencyModalData] = useState({ threeYearsCumulative: false, crownServant: false });
  const [residencyModalTargetId, setResidencyModalTargetId] = useState(null);

  // New state for naturalization details modal
  const [showNatDetailsModal, setShowNatDetailsModal] = useState(false);
  const [natDetailsData, setNatDetailsData] = useState({ naturalizedInNewfoundland: false, bucket: 'POST_1947' });
  const [natDetailsTargetId, setNatDetailsTargetId] = useState(null);

  // New state for advanced nat date modal
  const [showNatDateModal, setShowNatDateModal] = useState(false);
  const [natDateModalTargetId, setNatDateModalTargetId] = useState(null);
  const [natDateModalDate, setNatDateModalDate] = useState(null);

  // New state for parent status modal
  const [showParentStatusModal, setShowParentStatusModal] = useState(false);
  const [parentStatusModalTargetId, setParentStatusModalTargetId] = useState(null);
  const [parentStatusModalSelection, setParentStatusModalSelection] = useState(CITIZENSHIP_STATUS.NO);

  // New state for generic confirmation modal
  const [genericConfirmConfig, setGenericConfirmConfig] = useState({
    show: false,
    message: '',
    onConfirm: null,
  });

  useEffect(() => {
    if (category === 'descent' && !hasSeenDisclaimer) {
      setShowDisclaimer(true);
    }
  }, [category, hasSeenDisclaimer]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 1024);
      if (window.innerWidth >= 1024) setMobileMenuOpen(false);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Scroll to top on route change
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  const getGenerationLabel = (gen) => {
    if (gen === 0) return 'Applicant';
    if (gen === 1) return 'Parent';
    if (gen === 2) return 'Grandparent';
    return `${'Great-'.repeat(gen - 2)}Grandparent`;
  };


  const descentTabs = [
    { id: 'checker', label: 'Eligibility Checker' },
    { id: 'checklist', label: 'Document Checklist' },
    { id: 'apply', label: 'How to Apply' },
    { id: 'billc3', label: 'Bill C-3 (2025)' },
    { id: 'faq', label: 'FAQ' },
    { id: 'resources', label: 'Resources' },
  ];

  const afterTabs = [
    { id: 'passport', label: 'Passport' },
    { id: 'settling', label: 'Settling In' },
  ];

  const getNewPerson = (id, birthYear = new Date(1990, 0, 1)) => ({
    id,
    name: '',
    birthYear,
    isCitizen: CITIZENSHIP_STATUS.NO,
    citizenshipDate: null,
    ancestors: [],
    threeYearsCumulative: false,
    documentation: {},
    calculatedStatus: null,
    crownServant: false,
    alsoApplying: false,
  });

  const handleReset = () => {
    setGenericConfirmConfig({
      show: true,
      message: "Are you sure you want to reset the entire family tree?",
      onConfirm: () => setPeople([makeDefaultPerson()]),
    });
  };

  const handleModeSwitch = () => {
    setGenericConfirmConfig({
      show: true,
      message: "Are you sure you want to switch modes? The tree and dates will be preserved.",
      onConfirm: () => setUseExactDates(!useExactDates),
    });
  };

  const clearAllCalculations = (persons) => {
    for (const person of persons) {
      person.calculatedStatus = null;
      if (person.ancestors && person.ancestors.length > 0) {
        clearAllCalculations(person.ancestors);
      }
    }
  };

  const addAncestor = (personId, newAncestor) => {
    const newPeople = [...people];
    const add = (persons) => {
      for (let person of persons) {
        if (person.id === personId) {
          person.ancestors.push(newAncestor);
          return true;
        }
        if (person.ancestors && person.ancestors.length > 0) {
          if (add(person.ancestors)) return true;
        }
      }
      return false;
    };
    add(newPeople);
    clearAllCalculations(newPeople);
    setPeople(newPeople);
  };

  const handleAddAncestor = (personId, returnId = false) => {
    const newId = Math.max(...getAllIds(people)) + 1;

    let childBirthYear = new Date(1990, 0, 1);
    const findPerson = (list) => {
      for (const p of list) {
        if (p.id === personId) return p;
        if (p.ancestors) {
          const found = findPerson(p.ancestors);
          if (found) return found;
        }
      }
      return null;
    };
    const child = findPerson(people);
    if (child && child.birthYear) {
      childBirthYear = new Date(child.birthYear);
    }

    const newBirthYear = new Date(childBirthYear);
    newBirthYear.setFullYear(newBirthYear.getFullYear() - 20);

    const newAncestor = getNewPerson(newId, newBirthYear);
    addAncestor(personId, newAncestor);

    if (returnId) {
      return newId;
    }
  };

  const handleAddAncestorWithCheck = (personId) => {
    const findPerson = (list) => {
      for (const p of list) {
        if (p.id === personId) return p;
        if (p.ancestors) {
          const found = findPerson(p.ancestors);
          if (found) return found;
        }
      }
      return null;
    };
    const person = findPerson(people); // This is the child
    const limitDate = new Date(2025, 11, 15);

    // New logic for post-2025 children
    if (person.birthYear && new Date(person.birthYear) >= limitDate) {
      const newAncestorId = handleAddAncestor(personId, true);
      setParentStatusModalTargetId(newAncestorId);
      setParentStatusModalSelection(CITIZENSHIP_STATUS.NO);
      setShowParentStatusModal(true);
      return; // Stop here, modal will handle the rest
    }

    const effectiveStatus = person.calculatedStatus ?? person.isCitizen;

    if ([CITIZENSHIP_STATUS.BORN_IN_CANADA, CITIZENSHIP_STATUS.BY_DESCENT, CITIZENSHIP_STATUS.NATURALIZED].includes(effectiveStatus)) {
      setPendingAncestorId(personId);
      setShowConfirmModal(true);
    } else {
      handleAddAncestor(personId);
    }
  };

  const deepClone = (obj) => {
    if (obj === null || typeof obj !== 'object') return obj;
    if (obj instanceof Date) return new Date(obj);
    if (Array.isArray(obj)) return obj.map(deepClone);
    const cloned = {};
    for (const key in obj) {
      if (Object.prototype.hasOwnProperty.call(obj, key)) {
        cloned[key] = deepClone(obj[key]);
      }
    }
    return cloned;
  };

  const findPerson = (list, id) => {
    for (const p of list) {
      if (p.id === id) return p;
      if (p.ancestors) {
        const found = findPerson(p.ancestors, id);
        if (found) return found;
      }
    }
    return null;
  };

  const findChildOfAncestor = (list, ancestorId, gen = 0) => {
    for (const p of list) {
      if (p.ancestors && p.ancestors.some(a => a.id === ancestorId)) {
        return { ...p, generation: gen };
      }
      if (p.ancestors) {
        const found = findChildOfAncestor(p.ancestors, ancestorId, gen + 1);
        if (found) return found;
      }
    }
    return null;
  };

  const updatePersonInTree = (persons, id, newPersonData) => {
    for (let i = 0; i < persons.length; i++) {
      if (persons[i].id === id) {
        persons[i] = newPersonData;
        return true;
      }
      if (persons[i].ancestors && persons[i].ancestors.length > 0) {
        if (updatePersonInTree(persons[i].ancestors, id, newPersonData)) return true;
      }
    }
    return false;
  };

  const getPersonGeneration = (list, targetId, currentGen = 0) => {
    for (const p of list) {
      if (p.id === targetId) return currentGen;
      if (p.ancestors) {
        const gen = getPersonGeneration(p.ancestors, targetId, currentGen + 1);
        if (gen !== -1) return gen;
      }
    }
    return -1;
  };

  // Helper for the secondary modal actions
  const handleCascadeAnswer = (targetPersonId, updates) => {
    setPeople(prevPeople => {
      const newPeople = deepClone(prevPeople);
      const target = findPerson(newPeople, targetPersonId);
      if (target) {
        Object.assign(target, updates);
      }
      clearAllCalculations(newPeople);
      return newPeople;
    });
    setShowCascadeModal(false);
  };

  const triggerUnder21Modal = (personId, partialUpdate, child) => {
    setShowCascadeModal(false); // Close the "Born Before/After" modal first

    // A brief timeout to allow the state to update and modals to close/re-open smoothly
    setTimeout(() => {
      setCascadeMessage(`Was the ${getGenerationLabel(child.generation)} under 21 years old when the parent (${getGenerationLabel(child.generation + 1)}) naturalized?`);
      setCascadeButtonLabels({ yes: 'Yes, Under 21', no: 'No, 21 or Older' });
      setCascadeActions({
        onYes: () => { handleCascadeAnswer(personId, { ...partialUpdate, wasUnder21: true }); },
        onNo: () => { handleCascadeAnswer(personId, { ...partialUpdate, wasUnder21: false }); }
      });
      setShowCascadeModal(true);
    }, 100);
  };

  const handleNatDetailsSave = () => {
    if (!natDetailsTargetId) return;

    const { naturalizedInNewfoundland, bucket } = natDetailsData;
    let citizenshipDate;
    if (bucket === 'POST_1947') {
      citizenshipDate = new Date(1980, 0, 1);
    } else { // PRE_1947
      citizenshipDate = naturalizedInNewfoundland
        ? new Date(1949, 2, 31) // Pre-1949 for NFLD
        : new Date(1946, 11, 31); // Pre-1947 for Standard
    }

    const partialUpdate = { isCitizen: CITIZENSHIP_STATUS.NATURALIZED, naturalizedInNewfoundland, citizenshipDate };

    setShowNatDetailsModal(false);

    const child = findChildOfAncestor(people, natDetailsTargetId);
    if (child) {
      setCascadeMessage(`Was the ${getGenerationLabel(child.generation)} born BEFORE their parent (applicants ${getGenerationLabel(child.generation + 1)}) naturalized?`);
      // The variable `naturalizedBeforeBirth` is true if the PARENT naturalized before the CHILD was born.
      // So, if the CHILD was born before the PARENT naturalized, the variable should be false.
      setCascadeButtonLabels({ yes: 'Yes (Child born before)', no: 'No (Child born after)' });
      setCascadeActions({
        onYes: () => { // Born Before -> naturalizedBeforeBirth: false
          const finalUpdate = { ...partialUpdate, naturalizedBeforeBirth: false };
          if (bucket === 'PRE_1947') {
            triggerUnder21Modal(natDetailsTargetId, finalUpdate, child);
          } else {
            handleCascadeAnswer(natDetailsTargetId, finalUpdate);
          }
        },
        onNo: () => { handleCascadeAnswer(natDetailsTargetId, { ...partialUpdate, naturalizedBeforeBirth: true }); },
      });
      setShowCascadeModal(true);
    }
  };

  const handleNatDateSave = () => {
    if (!natDateModalTargetId) return;

    setPeople(prevPeople => {
      const newPeople = deepClone(prevPeople);
      const target = findPerson(newPeople, natDateModalTargetId);
      if (target) {
        target.citizenshipDate = natDateModalDate;
        target.naturalizedBeforeBirth = null;
        target.wasUnder21 = null;
      }
      clearAllCalculations(newPeople);
      return newPeople;
    });

    setShowNatDateModal(false);
    setNatDateModalTargetId(null);
    setNatDateModalDate(null);
  };

  const handleParentStatusSave = () => {
    if (!parentStatusModalTargetId) return;

    const status = parentStatusModalSelection;
    const targetId = parentStatusModalTargetId;

    const personToUpdate = findPerson(people, targetId);
    if (!personToUpdate) return;

    const updatedPerson = { ...personToUpdate, isCitizen: status };

    // Close the parent status modal
    setShowParentStatusModal(false);
    setParentStatusModalTargetId(null);

    // If the parent is a first-generation citizen (Born in Canada or Naturalized),
    // the 3-year residency rule does not apply to them for passing citizenship.
    // So, we just update their status and let the normal `onPersonChange` flow handle it
    // (which may trigger naturalization-specific modals).
    if (status === CITIZENSHIP_STATUS.BORN_IN_CANADA || status === CITIZENSHIP_STATUS.NATURALIZED) {
      onPersonChange(targetId, updatedPerson);
    } else {
      // For all other cases ('By Descent', 'No', 'Not Sure'), the 3-year rule *could* be relevant.
      // We ask now to gather the information.
      // First, apply the selected status to the person in the tree.
      const newPeople = deepClone(people);
      updatePersonInTree(newPeople, targetId, updatedPerson);
      setPeople(newPeople); // Update state so the card shows the selected status

      // Then, show the residency modal for that same person.
      setResidencyModalTargetId(targetId);
      setResidencyModalData({ threeYearsCumulative: false, crownServant: false });
      setShowResidencyModal(true);
    }
  };

  const onPersonChange = (personId, updatedPerson) => {
    const personBeforeUpdate = findPerson(people, personId);

    // If status changed to Naturalized in simplified mode, trigger popup
    // Check for Post-2025 Birth Date (Simplified Mode)
    // If the birth bucket changed to POST_2025, we need to ask about the "3 Years Residence" rule for the PARENT
    if (!useExactDates && updatedPerson.birthYear) {
      const getBirthBucket = (p) => {
        if (!p.birthYear) return 'PRE_2025';
        const d = new Date(p.birthYear);
        return d >= new Date(2025, 11, 15) ? 'POST_2025' : 'PRE_2025';
      };

      const oldBucket = getBirthBucket(personBeforeUpdate);
      const newBucket = getBirthBucket(updatedPerson);

      if (newBucket === 'POST_2025' && oldBucket === 'PRE_2025') {
        // We need to find the PARENT of the person we just updated.
        // Since our tree structure is Person -> Ancestors, the "Parent" is the first ancestor.
        const parent = updatedPerson.ancestors && updatedPerson.ancestors.length > 0 ? updatedPerson.ancestors[0] : null;

        if (parent) {
          const gen = getPersonGeneration(people, personId);
          setCascadeMessage(`Since the child (${getGenerationLabel(gen)}) is born after Dec 2025, has the parent (${getGenerationLabel(gen + 1)}) lived in Canada for at least 1,095 days (3 years)?`);
          setCascadeButtonLabels({ yes: 'Yes, 3 Years+', no: 'No, Less than 3 Years' });
          setCascadeActions({
            onYes: () => { handleCascadeAnswer(parent.id, { threeYearsCumulative: true }); },
            onNo: () => { handleCascadeAnswer(parent.id, { threeYearsCumulative: false }); }
          });
          setShowCascadeModal(true);
          // Note: We don't 'return' here because we still want the birth date update to apply immediately. 
          // The modal will then update the parent's residency flag asynchronously.
        }
      }
    }

    // Advanced mode naturalization
    if (useExactDates && updatedPerson.isCitizen === CITIZENSHIP_STATUS.NATURALIZED && personBeforeUpdate.isCitizen !== CITIZENSHIP_STATUS.NATURALIZED) {
      const newPeople = deepClone(people);
      const target = findPerson(newPeople, personId);
      if (target) {
        target.isCitizen = CITIZENSHIP_STATUS.NATURALIZED;
        target.calculatedStatus = null;
      }
      clearAllCalculations(newPeople);
      setPeople(newPeople);

      setNatDateModalTargetId(personId);
      setNatDateModalDate(personBeforeUpdate.citizenshipDate || null);
      setShowNatDateModal(true);
      return;
    }

    // This is the new entry point for the naturalization modal chain
    if (!useExactDates && updatedPerson.isCitizen === CITIZENSHIP_STATUS.NATURALIZED && personBeforeUpdate.isCitizen !== CITIZENSHIP_STATUS.NATURALIZED) {
      const child = findChildOfAncestor(people, personId);
      if (child) {
        setNatDetailsTargetId(personId);
        setNatDetailsData({ naturalizedInNewfoundland: personBeforeUpdate.naturalizedInNewfoundland || false, bucket: 'POST_1947' });
        setShowNatDetailsModal(true);
        return; // Halt the normal update, the modal will handle it.
      }
    }

    // Normal update flow
    const newPeople = deepClone(people);
    updatePersonInTree(newPeople, personId, updatedPerson);
    clearAllCalculations(newPeople);
    setPeople(newPeople);
  };

  const handleRemoveAncestor = (personId) => {
    const newPeople = [...people];
    const remove = (persons) => {
      for (const person of persons) {
        if (person.ancestors) {
          const index = person.ancestors.findIndex(p => p.id === personId);
          if (index !== -1) {
            person.ancestors.splice(index, 1);
            return true;
          }
          if (remove(person.ancestors)) return true;
        }
      }
      return false;
    };
    remove(newPeople);
    clearAllCalculations(newPeople);
    setPeople(newPeople);
  };

  const getAllIds = (persons) => {
    let ids = [];
    for (let person of persons) {
      ids.push(person.id);
      if (person.ancestors && person.ancestors.length > 0) {
        ids = ids.concat(getAllIds(person.ancestors));
      }
    }
    return ids;
  };

  const handleResidencyModalSave = () => {
    if (!residencyModalTargetId) return;

    const personToUpdate = findPerson(people, residencyModalTargetId);
    if (personToUpdate) {
      const updatedPerson = {
        ...personToUpdate,
        threeYearsCumulative: residencyModalData.threeYearsCumulative,
        crownServant: residencyModalData.crownServant,
      };
      onPersonChange(residencyModalTargetId, updatedPerson);
    }

    setShowResidencyModal(false);
    setResidencyModalTargetId(null);
  };

  const handleCalculate = () => {
    const deepClone = (obj) => {
      if (obj === null || typeof obj !== 'object') return obj;
      if (obj instanceof Date) return new Date(obj);
      if (Array.isArray(obj)) return obj.map(deepClone);
      const cloned = {};
      for (const key in obj) {
        if (Object.prototype.hasOwnProperty.call(obj, key)) {
          cloned[key] = deepClone(obj[key]);
        }
      }
      return cloned;
    };
    const newPeople = deepClone(people);
    clearAllCalculations(newPeople);

    const evaluatePerson = (person, child) => {
      // 1. Validate Birth Year: Ancestor cannot be born after Descendant
      // 2. Process Ancestors first (Post-order traversal)
      if (person.ancestors && person.ancestors.length > 0) {
        person.ancestors.forEach(ancestor => evaluatePerson(ancestor, person));
      }

      // 3. Calculate Citizenship (if not a root ancestor)
      if (person.ancestors && person.ancestors.length > 0) {
        const limitDate = new Date(2025, 11, 15);
        const birthDate = new Date(person.birthYear);

        let isCitizen = false;
        let hasUncertainty = false;

        for (const ancestor of person.ancestors) {
          const ancestorStatus = ancestor.calculatedStatus ?? ancestor.isCitizen;

          if (ancestorStatus === 'NOT_SURE') {
            hasUncertainty = true;
            continue;
          }

          if (![
            CITIZENSHIP_STATUS.BORN_IN_CANADA,
            CITIZENSHIP_STATUS.BY_DESCENT,
            CITIZENSHIP_STATUS.NATURALIZED
          ].includes(ancestorStatus)) continue;

          if (ancestorStatus === CITIZENSHIP_STATUS.NATURALIZED && !ancestor.citizenshipDate) {
            hasUncertainty = true;
            continue;
          }

          if (ancestor.citizenshipDate && ancestorStatus === CITIZENSHIP_STATUS.NATURALIZED) {
            const ancCitDate = new Date(ancestor.citizenshipDate);
            // Citizenship Act 1947 (Jan 1). Newfoundland joined April 1, 1949.
            const cutoffDate = ancestor.naturalizedInNewfoundland
              ? new Date(1949, 3, 1)
              : new Date(1947, 0, 1);
            // If strictly AFTER the cutoff, it's a Grant, so parent must be citizen before child birth.
            // LOGIC UPDATE: Use manual flag if present (Simplified Mode), otherwise compare dates.
            const isNatBeforeBirth = ancestor.naturalizedBeforeBirth ?? (birthDate > ancCitDate);

            if (ancCitDate > cutoffDate) {
              // If Naturalized AFTER birth (isNatBeforeBirth is false), child not eligible.
              if (!isNatBeforeBirth) continue;
            } else {
              // If ON or BEFORE the cutoff (Pre-1947/49), prior births are allowed ONLY if the child was a minor (under 21).
              // If Born Before Nat (isNatBeforeBirth is false) -> This implies child was born before the naturalization event.
              if (!isNatBeforeBirth) {
                let age = ancCitDate.getFullYear() - birthDate.getFullYear();
                const m = ancCitDate.getMonth() - birthDate.getMonth();
                if (m < 0 || (m === 0 && ancCitDate.getDate() < birthDate.getDate())) {
                  age--;
                }
                // Use manual flag if present, otherwise use calculated age
                const isUnder21 = ancestor.wasUnder21 ?? (age < 21);
                if (!isUnder21) continue;
              }
            }
          }

          // If ancestor is Born in Canada or Naturalized, they are "First Generation" (in Canada).
          // Their children (the person being evaluated) are eligible automatically.
          if ([
            CITIZENSHIP_STATUS.BORN_IN_CANADA,
            CITIZENSHIP_STATUS.NATURALIZED
          ].includes(ancestorStatus)) {
            isCitizen = true;
            break;
          }

          // If ancestor is Born Outside Canada (Descent), limits apply.
          if (ancestorStatus === CITIZENSHIP_STATUS.BY_DESCENT) {
            // Pre-2025: No generational limit (Automatic)
            if (birthDate < limitDate) {
              isCitizen = true;
              break;
            }
            // Post-2025: Requires 3 years cumulative presence OR Crown Servant status
            if (ancestor.threeYearsCumulative === true || ancestor.crownServant === true) {
              isCitizen = true;
              break;
            }
          }
        }

        let newStatus = CITIZENSHIP_STATUS.NO;
        if (isCitizen) newStatus = CITIZENSHIP_STATUS.BY_DESCENT;
        else if (hasUncertainty) newStatus = 'NOT_SURE';

        let finalStatus = newStatus;
        // If person is Born in Canada, do not override with By Descent.
        if (person.isCitizen === CITIZENSHIP_STATUS.BORN_IN_CANADA) {
          finalStatus = null;
        }
        // If calculation is NO/NOT_SURE, but user manually said YES, respect the user (return null to fallback)
        else if ((newStatus === CITIZENSHIP_STATUS.NO || newStatus === 'NOT_SURE') && person.isCitizen !== CITIZENSHIP_STATUS.NO && person.isCitizen !== 'NOT_SURE') {

          finalStatus = null;
        }

        let statusChanged = person.calculatedStatus !== finalStatus;
        if (statusChanged) {
          person.calculatedStatus = finalStatus;
        }

        if (finalStatus === CITIZENSHIP_STATUS.BY_DESCENT) {
          const pCitDate = person.citizenshipDate ? new Date(person.citizenshipDate) : null;
          if (!pCitDate || pCitDate.getTime() !== birthDate.getTime()) {
            person.citizenshipDate = birthDate;
          }
        } else if (finalStatus === CITIZENSHIP_STATUS.NO) { // Only clear if explicitly NO
          if (person.citizenshipDate !== null) {
            person.citizenshipDate = null;
          }
        }
      } else {
        // Root ancestor, no calculated status
        if (person.calculatedStatus !== null) {
          person.calculatedStatus = null;
        }
      }
    };

    newPeople.forEach(p => evaluatePerson(p, null));

    setPeople(newPeople);

    if (summaryRef.current) {
      summaryRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const handleCategoryChange = (id) => {
    if (isMobile) setMobileMenuOpen(false);
    if (id === 'home') navigate('/');
    else if (id === 'descent') navigate('/descent/checker');
    else if (id === 'after') navigate('/after/passport');
    else if (id === 'land_acknowledgement') navigate('/land_acknowledgement');
    else if (id === 'about') navigate('/about');
  };

  const handleTabChange = (id, targetCategory) => {
    if (isMobile) setMobileMenuOpen(false);
    const cat = targetCategory || category;
    if (cat === 'descent') navigate(`/descent/${id}`);
    if (cat === 'after') navigate(`/after/${id}`);
  };

  const acceptDisclaimer = () => {
    setHasSeenDisclaimer(true);
    setShowDisclaimer(false);
  };

  return (
    <div className="App" style={{ display: 'flex', minHeight: '100vh', flexDirection: 'column' }}>
      {showDisclaimer && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 100,
          display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px'
        }}>
          <div style={{
            backgroundColor: 'white', padding: '24px', borderRadius: '12px',
            maxWidth: '500px', width: '100%', textAlign: 'center',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
          }}>
            <h2 style={{ marginTop: 0, color: '#111827' }}>Legal Disclaimer</h2>
            <p style={{ color: '#4b5563', lineHeight: '1.5', marginBottom: '24px' }}>
              We are not lawyers or immigration consultants. The information provided on this website is for educational purposes only and is not legal advice.
              <br /><br />
              Outcomes depend on individual circumstances and official government assessment. Please verify all information with IRCC or a qualified legal professional.
            </p>
            <button
              onClick={acceptDisclaimer}
              style={{ padding: '12px 24px', backgroundColor: '#2563eb', color: 'white', border: 'none', borderRadius: '6px', fontSize: '1rem', fontWeight: '600', cursor: 'pointer', width: '100%' }}
            >
              I Understand
            </button>
          </div>
        </div>
      )}

      {/* Custom Confirmation Modal */}
      {showConfirmModal && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 200,
          display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px'
        }}>
          <div style={{
            backgroundColor: 'white', padding: '24px', borderRadius: '12px',
            maxWidth: '400px', width: '100%', textAlign: 'center',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)'
          }}>
            <h3 style={{ marginTop: 0, color: '#111827' }}>Already Canadian?</h3>
            <p style={{ color: '#4b5563', marginBottom: '24px' }}>
              This person is already marked as a Canadian citizen. Are you sure you need to add another parent?
            </p>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
              <button
                onClick={() => setShowConfirmModal(false)}
                style={{ padding: '10px 20px', backgroundColor: 'white', border: '1px solid #d1d5db', borderRadius: '6px', cursor: 'pointer', color: '#374151' }}
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  handleAddAncestor(pendingAncestorId);
                  setShowConfirmModal(false);
                }}
                style={{ padding: '10px 20px', backgroundColor: '#2563eb', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: '600' }}
              >
                Yes, Add Parent
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Cascade Update Modal */}
      {showCascadeModal && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 200,
          display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px'
        }}>
          <div style={{
            backgroundColor: 'white', padding: '24px', borderRadius: '12px',
            maxWidth: '400px', width: '100%', textAlign: 'center',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)'
          }}>
            <h3 style={{ marginTop: 0, color: '#111827' }}>Update Related Events?</h3>
            <p style={{ color: '#4b5563', marginBottom: '24px' }}>
              {cascadeMessage}
            </p>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
              <button
                onClick={cascadeActions?.onNo}
                style={{ padding: '10px 20px', backgroundColor: 'white', border: '1px solid #d1d5db', borderRadius: '6px', cursor: 'pointer', color: '#374151' }}
              >
                {cascadeButtonLabels.no}
              </button>
              <button
                onClick={cascadeActions?.onYes}
                style={{ padding: '10px 20px', backgroundColor: '#2563eb', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: '600' }}
              >
                {cascadeButtonLabels.yes}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Residency Modal */}
      {showResidencyModal && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 200,
          display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px'
        }}>
          <div style={{
            backgroundColor: 'white', padding: '24px', borderRadius: '12px',
            maxWidth: '500px', width: '100%',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)'
          }}>
            <h3 style={{ marginTop: 0, color: '#111827' }}>Substantial Connection Test</h3>
            <p style={{ color: '#4b5563', marginBottom: '24px', textAlign: 'left' }}>
              For a child born on or after December 15, 2025, the Canadian parent must meet one of the following conditions to pass on citizenship. Please select all that apply for the new parent.
            </p>
            <div style={{ textAlign: 'left', display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '24px' }}>
              <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', fontSize: '0.9rem', color: '#334155' }}>
                <input
                  type="checkbox"
                  checked={residencyModalData.threeYearsCumulative}
                  onChange={(e) => setResidencyModalData(d => ({ ...d, threeYearsCumulative: e.target.checked }))}
                  style={{ width: '16px', height: '16px', marginRight: '12px', accentColor: '#2563eb' }}
                />
                Parent lived 1,095 cumulative days in Canada (3 Years) before child's birth.
              </label>
              <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', fontSize: '0.9rem', color: '#334155' }}>
                <input
                  type="checkbox"
                  checked={residencyModalData.crownServant}
                  onChange={(e) => setResidencyModalData(d => ({ ...d, crownServant: e.target.checked }))}
                  style={{ width: '16px', height: '16px', marginRight: '12px', accentColor: '#2563eb' }}
                />
                Parent was a Crown Servant at time of child's birth.
              </label>
            </div>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowResidencyModal(false)}
                style={{ padding: '10px 20px', backgroundColor: 'white', border: '1px solid #d1d5db', borderRadius: '6px', cursor: 'pointer', color: '#374151' }}
              >
                Cancel
              </button>
              <button
                onClick={handleResidencyModalSave}
                style={{ padding: '10px 20px', backgroundColor: '#2563eb', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: '600' }}
              >
                Save and Continue
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Naturalization Details Modal */}
      {showNatDetailsModal && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 200,
          display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px'
        }}>
          <div style={{
            backgroundColor: 'white', padding: '24px', borderRadius: '12px',
            maxWidth: '500px', width: '100%',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)'
          }}>
            <h3 style={{ marginTop: 0, color: '#111827' }}>Naturalization Details</h3>
            <p style={{ color: '#4b5563', marginBottom: '24px', textAlign: 'left' }}>
              Please provide more details about this ancestor's naturalization to apply the correct historical rules.
            </p>
            <div style={{ textAlign: 'left', display: 'flex', flexDirection: 'column', gap: '16px', marginBottom: '24px' }}>
              <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', fontSize: '0.9rem', color: '#334155' }}>
                <input
                  type="checkbox"
                  checked={natDetailsData.naturalizedInNewfoundland}
                  onChange={(e) => setNatDetailsData(d => ({ ...d, naturalizedInNewfoundland: e.target.checked }))}
                  style={{ width: '16px', height: '16px', marginRight: '12px', accentColor: '#ea580c' }}
                />
                Naturalized in Newfoundland or Labrador?
              </label>
              <div>
                <label style={{ display: 'block', marginBottom: '6px', fontSize: '0.875rem', fontWeight: '600', color: '#374151' }}>Naturalization Period</label>
                <select
                  value={natDetailsData.bucket}
                  onChange={(e) => setNatDetailsData(d => ({ ...d, bucket: e.target.value }))}
                  style={{ width: '100%', padding: '8px 12px', borderRadius: '6px', border: '1px solid #d1d5db', fontSize: '1rem' }}
                >
                  <option value="POST_1947">
                    After {natDetailsData.naturalizedInNewfoundland ? 'April 1, 1949' : 'January 1, 1947'}
                  </option>
                  <option value="PRE_1947">
                    Before {natDetailsData.naturalizedInNewfoundland ? 'April 1, 1949' : 'January 1, 1947'}
                  </option>
                </select>
              </div>
            </div>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowNatDetailsModal(false)}
                style={{ padding: '10px 20px', backgroundColor: 'white', border: '1px solid #d1d5db', borderRadius: '6px', cursor: 'pointer', color: '#374151' }}
              >
                Cancel
              </button>
              <button
                onClick={handleNatDetailsSave}
                style={{ padding: '10px 20px', backgroundColor: '#2563eb', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: '600' }}
              >
                Save and Continue
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Naturalization Date Modal (Advanced) */}
      {showNatDateModal && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 200,
          display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px'
        }}>
          <div style={{
            backgroundColor: 'white', padding: '24px', borderRadius: '12px',
            maxWidth: 'fit-content',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)'
          }}>
            <h3 style={{ marginTop: 0, color: '#111827' }}>Select Naturalization Date</h3>
            <p style={{ color: '#4b5563', marginBottom: '24px', textAlign: 'left', maxWidth: '300px' }}>
              Please select the date this ancestor was naturalized as a Canadian citizen.
            </p>
            <div style={{ marginBottom: '24px', display: 'flex', justifyContent: 'center' }}>
              <Flatpickr
                value={natDateModalDate}
                onChange={([date]) => setNatDateModalDate(date)}
                options={{
                  dateFormat: 'Y-m-d',
                  allowInput: true,
                  inline: true,
                  altInput: true, // Renders a separate input field
                  altFormat: 'F j, Y', // Formats the date in the input field
                }}
                placeholder="Select Date"
              />
            </div>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowNatDateModal(false)}
                style={{ padding: '10px 20px', backgroundColor: 'white', border: '1px solid #d1d5db', borderRadius: '6px', cursor: 'pointer', color: '#374151' }}
              >
                Cancel
              </button>
              <button
                onClick={handleNatDateSave}
                style={{ padding: '10px 20px', backgroundColor: '#2563eb', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: '600' }}
              >
                Save Date
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Parent Status Modal */}
      {showParentStatusModal && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 200,
          display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px'
        }}>
          <div style={{
            backgroundColor: 'white', padding: '24px', borderRadius: '12px',
            maxWidth: '500px', width: '100%',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)'
          }}>
            <h3 style={{ marginTop: 0, color: '#111827' }}>Parent's Citizenship Status</h3>
            <p style={{ color: '#4b5563', marginBottom: '24px', textAlign: 'left' }}>
              Because the child was born on or after December 15, 2025, please specify the new parent's Canadian citizenship status.
            </p>
            <div style={{ marginBottom: '24px' }}>
              <label style={{ display: 'block', marginBottom: '6px', fontSize: '0.875rem', fontWeight: '600', color: '#374151' }}>Is the parent a Canadian Citizen?</label>
              <select
                value={parentStatusModalSelection}
                onChange={(e) => setParentStatusModalSelection(e.target.value)}
                style={{ width: '100%', padding: '8px 12px', borderRadius: '6px', border: '1px solid #d1d5db', fontSize: '1rem' }}
              >
                <option value={CITIZENSHIP_STATUS.NO}>No</option>
                <option value="NOT_SURE">Not sure / Trying to Determine</option>
                <option value={CITIZENSHIP_STATUS.BORN_IN_CANADA}>Yes (Born in Canada)</option>
                <option value={CITIZENSHIP_STATUS.BY_DESCENT}>Yes (By Descent, Recognized Prior toDecember 15, 2025)</option>
                <option value={CITIZENSHIP_STATUS.NATURALIZED}>Yes (Naturalized)</option>
              </select>
            </div>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => {
                  setShowParentStatusModal(false);
                  handleRemoveAncestor(parentStatusModalTargetId);
                  setParentStatusModalTargetId(null);
                }}
                style={{ padding: '10px 20px', backgroundColor: 'white', border: '1px solid #d1d5db', borderRadius: '6px', cursor: 'pointer', color: '#374151' }}
              >
                Cancel
              </button>
              <button
                onClick={handleParentStatusSave}
                style={{ padding: '10px 20px', backgroundColor: '#2563eb', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: '600' }}
              >
                Continue
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Generic Confirmation Modal */}
      {genericConfirmConfig.show && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 200,
          display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px'
        }}>
          <div style={{
            backgroundColor: 'white', padding: '24px', borderRadius: '12px',
            maxWidth: '400px', width: '100%', textAlign: 'center',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)'
          }}>
            <h3 style={{ marginTop: 0, color: '#111827' }}>Are you sure?</h3>
            <p style={{ color: '#4b5563', marginBottom: '24px' }}>
              {genericConfirmConfig.message}
            </p>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
              <button
                onClick={() => setGenericConfirmConfig({ show: false, message: '', onConfirm: null })}
                style={{ padding: '10px 20px', backgroundColor: 'white', border: '1px solid #d1d5db', borderRadius: '6px', cursor: 'pointer', color: '#374151' }}
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  if (genericConfirmConfig.onConfirm) genericConfirmConfig.onConfirm();
                  setGenericConfirmConfig({ show: false, message: '', onConfirm: null });
                }}
                style={{ padding: '10px 20px', backgroundColor: '#2563eb', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: '600' }}
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Mobile Header */}
      {isMobile && (
        <div style={{
          height: '60px', borderBottom: '1px solid #e5e7eb', display: 'flex', alignItems: 'center',
          padding: '0 16px', justifyContent: 'space-between', position: 'sticky', top: 0,
          backgroundColor: 'white', zIndex: 50
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }} onClick={() => navigate('/')}>
            <img src="/logo2.png" alt="Logo" style={{ height: '32px' }} />
            <span style={{ fontWeight: 'bold', fontSize: '1.2rem', color: '#111827' }}>Lost & Found Canadians</span>
          </div>
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            style={{ background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer', color: '#374151' }}
          >
            {mobileMenuOpen ? '✕' : '☰'}
          </button>
        </div>
      )}

      <div style={{ display: 'flex', flex: 1, position: 'relative' }}>
        {/* Sidebar - Conditional Render for Mobile */}
        {(!isMobile || mobileMenuOpen) && (
          <div style={isMobile ? { position: 'fixed', top: '60px', left: 0, bottom: 0, width: '260px', zIndex: 40, backgroundColor: 'white', borderRight: '1px solid #e5e7eb', overflowY: 'auto' } : {}}>
            <Sidebar
              currentCategory={category}
              onCategoryChange={handleCategoryChange}
              currentTab={view}
              onTabChange={handleTabChange}
              descentTabs={descentTabs}
              afterTabs={afterTabs}
            />
          </div>
        )}

        {/* Mobile Backdrop */}
        {isMobile && mobileMenuOpen && (
          <div onClick={() => setMobileMenuOpen(false)} style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 30 }} />
        )}

        <div className="main-content" style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0, marginLeft: isMobile ? 0 : '260px' }}>
          <Routes>
            {/* HOME ROUTE */}
            <Route path="/" element={
              <div style={{ padding: isMobile ? '24px' : '60px', textAlign: 'center', maxWidth: '800px', margin: '0 auto' }}>
                <img src="/logo2.png" alt="Lost & Found Logo" style={{ width: '150px', marginBottom: '20px' }} />
                <h1 style={{ fontSize: '2.5rem', color: '#111827', marginBottom: '20px' }}>Welcome to Lost & Found Canadians!</h1>
                <p style={{ fontSize: '1.2rem', color: '#4b5563', marginBottom: '40px' }}>
                  Your all-in-one guide to Canadian citizenship, passports, and settling in.
                </p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '20px', maxWidth: '600px', margin: '0 auto' }}>
                  <div
                    onClick={() => navigate('/descent/checker')}
                    style={{ padding: '24px', backgroundColor: 'white', borderRadius: '12px', border: '1px solid #e5e7eb', cursor: 'pointer', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)' }}
                  >
                    <div style={{ fontSize: '2rem', marginBottom: '10px' }}>🍁</div>
                    <h3 style={{ margin: '0 0 8px 0', color: '#111827' }}>Citizenship By Descent</h3>
                    <p style={{ margin: 0, color: '#6b7280' }}>Check your eligibility, including under the Bill C-3 rules that came into effect December 2025.</p>
                  </div>
                  <div
                    onClick={() => navigate('/after/passport')}
                    style={{ padding: '24px', backgroundColor: 'white', borderRadius: '12px', border: '1px solid #e5e7eb', cursor: 'pointer', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)' }}
                  >
                    <div style={{ fontSize: '2rem', marginBottom: '10px' }}>🛂</div>
                    <h3 style={{ margin: '0 0 8px 0', color: '#111827' }}>After Citizenship</h3>
                    <p style={{ margin: 0, color: '#6b7280' }}>Next steps: Passport, SIN, Health Card, and more.</p>
                  </div>
                  <div
                    onClick={() => navigate('/land_acknowledgement')}
                    style={{ padding: '24px', backgroundColor: 'white', borderRadius: '12px', border: '1px solid #e5e7eb', cursor: 'pointer', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)' }}
                  >
                    <div style={{ fontSize: '2rem', marginBottom: '10px' }}>🌎</div>
                    <h3 style={{ margin: '0 0 8px 0', color: '#111827' }}>Land Acknowledgement</h3>
                    <p style={{ margin: 0, color: '#6b7280' }}>Learn about the traditional territories and treaties of Indigenous Peoples.</p>
                  </div>
                </div>
              </div>
            } />

            {/* CITIZENSHIP BY DESCENT ROUTES */}
            <Route path="/descent/*" element={
              <>
                <NavBar title="Citizenship By Descent" tabs={descentTabs} currentTab={view} onTabChange={handleTabChange} />
                <ProgressBar currentPath={location.pathname} onNavigate={navigate} />
                <Suspense fallback={<div style={{ padding: '40px', textAlign: 'center', color: '#6b7280' }}>Loading...</div>}>
                  <Routes>
                    <Route path="checker" element={
                      <div style={{ textAlign: 'center', paddingBottom: '60px' }}>
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px', margin: '40px 0' }}>
                          <h1 style={{ margin: 0, color: '#111827' }}>Canadian Citizenship by Descent</h1>
                          <p>Determine your eligibility under the new Bill C-3 rules. Use the tree-builder and the document checklist to understand your path to citizenship.</p>
                          <p style={{ color: '#6b7280', maxWidth: '600px' }}>Note: For exact birth and naturalization dates, use Advanced Mode.</p>
                          <div style={{ display: 'flex', gap: '10px', marginTop: '10px', flexWrap: 'wrap', justifyContent: 'center', alignItems: 'center' }}>
                            <button onClick={handleCalculate} style={primaryActionButtonStyle}>Calculate Eligibility</button>
                            <button onClick={handleReset} style={actionButtonStyle}>Reset Tree</button>
                            <button onClick={() => setShowInlineDocs(!showInlineDocs)} style={actionButtonStyle}>{showInlineDocs ? 'Hide Docs in Tree' : 'Show Docs in Tree'}</button>
                            <button onClick={handleModeSwitch} style={actionButtonStyle}>{useExactDates ? 'Simplified Mode' : 'Advanced Mode'}</button>
                          </div>
                        </div>
                        <div ref={summaryRef} style={{ scrollMarginTop: '120px' }}>
                          <Summary
                            person={people[0]}
                            onNextSteps={() => navigate('/descent/apply')}
                            onViewChecklist={() => navigate('/descent/checklist')}
                            onBillC3={() => navigate('/descent/billc3')}
                            onFAQ={() => navigate('/descent/faq')}
                            onResources={() => navigate('/descent/resources')}
                          />
                        </div>
                        {people.map(person => (
                          <Person
                            key={person.id}
                            person={person}
                            onPersonChange={onPersonChange}
                            onAddAncestor={handleAddAncestorWithCheck}
                            onRemoveAncestor={handleRemoveAncestor}
                            onCalculate={handleCalculate}
                            generation={0}
                            showDocs={showInlineDocs}
                            useExactDates={useExactDates}
                            isMobile={isMobile}
                          />
                        ))}
                        <div style={{ marginTop: '40px', padding: '20px', backgroundColor: '#f8fafc', borderRadius: '8px', maxWidth: '600px', marginLeft: 'auto', marginRight: 'auto', border: '1px solid #e2e8f0' }}>
                          <p style={{ color: '#475569', marginBottom: '12px', fontSize: '0.95rem' }}>
                            <strong>Did we miss something?</strong>
                            <br />
                            Citizenship rules can be tricky. If you think you qualify but the tool says otherwise, let us know so we can improve.
                          </p>
                          <button
                            onClick={() => window.open("https://docs.google.com/forms/d/e/1FAIpQLSe9u5NxYWLSll0qrxalBRk8bVKQtZKEHp27hoU8m94C76aE4w/viewform?usp=dialog", "_blank")}
                            style={{
                              padding: '8px 16px',
                              backgroundColor: '#ffffff',
                              color: '#4b5563',
                              border: '1px solid #d1d5db',
                              borderRadius: '6px',
                              cursor: 'pointer',
                              fontSize: '0.9rem'
                            }}
                          >
                            Submit Feedback
                          </button>
                          <p style={{ marginTop: '16px', fontSize: '0.8rem', color: '#9ca3af' }}>
                            <strong>Privacy Note:</strong> No data is sent to any server.
                            Your tree exists only for your current session and is gone when you close the tab.
                          </p>
                        </div>
                      </div>
                    } />
                    <Route path="checklist" element={<DocumentChecklist people={people} onPersonChange={onPersonChange} isMobile={isMobile} />} />
                    <Route path="billc3" element={<BillC3Info />} />
                    <Route path="faq" element={<FAQ />} />
                    <Route path="resources" element={<Resources />} />
                    <Route path="apply" element={<NextSteps onViewChecklist={() => navigate('/descent/checklist')} />} />
                    <Route path="*" element={<Navigate to="checker" />} />
                  </Routes>
                </Suspense>
                <div style={{ padding: '24px', textAlign: 'center', fontSize: '0.85rem', color: '#6b7280', backgroundColor: '#f9fafb', borderTop: '1px solid #e5e7eb' }}>
                  <p style={{ margin: 0, maxWidth: '800px', marginLeft: 'auto', marginRight: 'auto' }}>
                    <strong>Legal Disclaimer:</strong> We are not lawyers or immigration consultants.
                    The information provided on this website is for educational purposes only and is not legal advice.
                    Outcomes depend on individual circumstances and official government assessment.
                    Please verify all information with <a href="https://www.canada.ca/en/immigration-refugees-citizenship.html" target="_blank" rel="noreferrer" style={{ color: '#2563eb', textDecoration: 'underline' }}>Immigration, Refugees and Citizenship Canada (IRCC)</a> or a qualified legal professional.
                  </p>
                </div>
              </>
            } />

            {/* AFTER CITIZENSHIP ROUTES */}
            <Route path="/after/*" element={
              <>
                <NavBar title="After Citizenship" tabs={afterTabs} currentTab={view} onTabChange={handleTabChange} />
                <ProgressBar currentPath={location.pathname} onNavigate={navigate} />
                <Suspense fallback={<div style={{ padding: '40px', textAlign: 'center', color: '#6b7280' }}>Loading...</div>}>
                  <Routes>
                    <Route path="passport" element={<PassportGuide onGoToChecker={() => navigate('/descent/checker')} onGoToApply={() => navigate('/descent/apply')} />} />
                    <Route path="settling" element={<SettlingIn />} />
                    <Route path="*" element={<Navigate to="passport" />} />
                  </Routes>
                </Suspense>
              </>
            } />

            {/* LAND ACKNOWLEDGEMENT ROUTE */}
            <Route path="/land_acknowledgement" element={
              <Suspense fallback={<div style={{ padding: '40px', textAlign: 'center', color: '#6b7280' }}>Loading...</div>}>
                <LandAcknowledgement />
              </Suspense>
            } />

            {/* ABOUT ROUTE */}
            <Route path="/about" element={
              <Suspense fallback={<div style={{ padding: '40px', textAlign: 'center', color: '#6b7280' }}>Loading...</div>}>
                <About />
              </Suspense>
            } />

            {/* FALLBACK */}
            <Route path="*" element={<Navigate to="/" />} />
          </Routes>
        </div>
      </div>
    </div>
  );
};

const App = () => {
  return (
    <BrowserRouter>
      <AppContent />
    </BrowserRouter>
  );
};

export default App;